import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import classNames from 'classnames';
import lodashTemplate from 'lodash/template';

import {
  PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_ALL,
  PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_RETAIL,
  PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_INTERNET_AMENITY,
  UTILITY_ACTIVATION_ITEM_PURCHASE_REQUEST_TERMS_AND_CONDITIONS,
  UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID,
  UTILITY_ACTIVATION_ITEM_PURCHASE_REQUEST_TERMS_AND_CONDITIONS_HYBRID_PLANS,
  UTILITY_PROVIDER_ID_XFINITY,
  XFINITY_ADDITIONAL_TERMS,
} from 'common/utility/constants';

import isCardNumber from 'common/util/isCardNumber';
import isCardExpirationSameYear from 'common/util/isCardExpirationSameYear';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';

import useBranch from 'client/hooks/useBranch';

import {
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  validateFormFieldsAndSubmit,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import { SR_MARKDOWN_THEME_OUTLINED_HELVETICA } from 'client/components/SrMarkdown';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrTextGreycliff from 'client/components/SrTextGreycliff';
import SrUtilityProfitProviderPlanCard, {
  PROVIDER_PLAN_CARD_THEME_CARRIER,
  PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY,
} from 'client/components/SrUtilityProfitProviderPlanCard/SrUtilityProfitProviderPlanCard';
import SrUtilityProfitAmenityConfirmationPlanCard from 'client/components/SrUtilityProfitAmenityConfirmationPlanCard/SrUtilityProfitAmenityConfirmationPlanCard';

import './SrUtilitySetupProviderPlanConfirmation.less';
import analytics from 'client/analytics';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

// Extracted reusable function for parsing experiment variants cookie
function getParsedExperimentVariants() {
  const experimentVariantsRaw = getCookie('sr-utility-experiment-active-variants');
  let experimentVariants = experimentVariantsRaw
    ? decodeURIComponent(experimentVariantsRaw)
    : undefined;
  if (experimentVariants?.startsWith('j:')) {
    try {
      experimentVariants = JSON.parse(experimentVariants.slice(2));
    } catch (e) {
      // fallback to the raw string if parsing fails
    }
  }
  return experimentVariants;
}

const checkIfIsAmericanExpressCard = (cardNumber) => {
  const firstTwoDigits = parseInt(`${cardNumber}`.slice(0, 2), 10);

  return firstTwoDigits === 34 || firstTwoDigits === 37;
};

const isUserValidPhoneNumber = (utilityUser) => {
  return utilityUser?.phone && !utilityUser?.phone.includes('555');
};

const getFormFieldIds = ({
  utilityProvider,
  utilityUser,
  isInternetAmenityPlan,
  isHybridPurchaseFlowType,
}) => {
  const isValidPhoneNumber = isUserValidPhoneNumber(utilityUser);

  if (isInternetAmenityPlan || isHybridPurchaseFlowType) {
    return {
      cardFormFieldIds: [
        'cardAddressLine1',
        'cardAddressLine2',
        'cardAddressCity',
        'cardAddressState',
        'cardName',
        'cardNumber',
        'expirationDate',
        'cvc',
        'zipCode',
        'agreeToTermsAndConditions',
      ],
      securityQuestionFieldIds: [
        ...utilityProvider.questions
          .filter((question) =>
            [
              PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_INTERNET_AMENITY,
              PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_ALL,
            ].includes(question.compatiblePurchaseFlowType),
          )
          .map((question) => question.fieldId),
      ],
    };
  }

  return {
    cardFormFieldIds: [
      'cardAddressLine1',
      'cardAddressLine2',
      'cardAddressCity',
      'cardAddressState',
      'cardName',
      'cardNumber',
      'expirationDate',
      'cvc',
      'zipCode',
      'agreeToTermsAndConditions',
    ],
    securityQuestionFieldIds: [
      ...(!isValidPhoneNumber ? ['verifiedPhoneNumber'] : []),
      ...utilityProvider.questions
        .filter((question) =>
          [
            PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_RETAIL,
            PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_ALL,
          ].includes(question.compatiblePurchaseFlowType),
        )
        .map((question) => question.fieldId),
    ],
  };
};

const getInternetAmenityFormFields = ({
  utilityProvider,
  orderPlaced,
  values,
  formikBag,
  termsAndConditionsHybridPlans,
}) => {
  const isAmericanExpress = checkIfIsAmericanExpressCard(values.cardNumber);
  return [
    {
      id: 'utilityProviderPlanId',
      type: 'hidden',
    },
    {
      id: 'utilityProviderPlanVariantId',
      type: 'hidden',
    },
    {
      id: 'internetAmenityPlanId',
      type: 'hidden',
    },
    showFieldIf(utilityProvider.ccRequired, {
      title: 'Address Line 2',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 2 here...',
        dataDogMasked: true,
      },
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressLine1',
      title: 'Address Line 1',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 1 here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressLine2',
      title: 'Address Line 2',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 2 here...',
        dataDogMasked: true,
      },
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressCity',
      title: 'City',
      type: 'short_text',
      properties: {
        placeholder: 'Type city here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressState',
      title: 'State',
      type: 'short_text',
      properties: {
        placeholder: 'Type state here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardName',
      title: 'Name on Card',
      type: 'short_text',
      properties: {
        placeholder: 'Type the name on your card...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardNumber',
      title: 'Card Number',
      type: 'card_number',
      properties: {
        placeholder: 'Type card number here...',
        onlyAcceptNumbers: true,
        length: isAmericanExpress ? 15 : 16,
        lengthErrorMessage: 'Your card number is incomplete.',
        dataDogMasked: true,
        widget:
          formikBag && formikBag.touched?.cardNumber && !isCardNumber(values.cardNumber) ? (
            <SrBox margin="3px 0 12px">
              <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                <SrTextGreycliff color="white" weight="700">
                  {'Check Your Card Number: '}
                </SrTextGreycliff>
                <SrTextGreycliff color="white" weight="600">
                  This card type isn't recognized. Double-check your number or proceed anyway.
                </SrTextGreycliff>
              </SrErrorMessage>
            </SrBox>
          ) : null,
      },
      validations: ['required', 'length'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'expirationDate',
      title: 'Expiration Date',
      type: 'date_expiration_input',
      properties: {
        placeholder: 'Type expiration date here...',
        dateFormat: 'MM/YY',
        dataDogMasked: true,
        widget:
          formikBag &&
          formikBag.touched?.expirationDate &&
          isCardExpirationSameYear(values.expirationDate) ? (
            <SrBox margin="3px 0 12px">
              <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                <SrTextGreycliff color="white" weight="700">
                  {'Check Your Expiration Date: '}
                </SrTextGreycliff>
                <SrTextGreycliff color="white" weight="600">
                  Your card expires soon. Please double-check if the year is correct because some
                  internet companies may reject it.
                </SrTextGreycliff>
              </SrErrorMessage>
            </SrBox>
          ) : null,
      },
      validations: ['required', 'noPastDates'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cvc',
      title: 'Security Code',
      type: 'card_cvc',
      properties: {
        placeholder: 'Type security code here...',
        onlyAcceptNumbers: true,
        length: isAmericanExpress ? 4 : 3,
        lengthErrorMessage: "Your card's security code is incomplete.",
        dataDogMasked: true,
      },
      validations: ['required', 'length'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'zipCode',
      title: 'ZIP Code Associated With Card',
      type: 'zipCode',
      properties: {
        placeholder: 'Type ZIP code here...',
        dataDogMasked: true,
      },
      validations: ['required', 'zipCode'],
    }),
    {
      id: 'agreeToTermsAndConditions',
      title: 'Terms & Conditions',
      type: 'markdown_checkbox',
      properties: {
        children: (
          <SrText fontSize="15px" font="helvetica" lineHeight="20px" weight="500">
            I have read and agree to the Terms & Conditions.
          </SrText>
        ),
        className: 'adobeCheckboxSquare16 helvetica',
        markdown: {
          theme: SR_MARKDOWN_THEME_OUTLINED_HELVETICA,
          source: termsAndConditionsHybridPlans,
          styles: {
            height: '168px',
            overflowX: 'scroll',
          },
        },
      },
      validations: ['required', 'legal'],
    },
    ...utilityProvider.questions
      .filter((question) =>
        [
          PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_INTERNET_AMENITY,
          PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_ALL,
        ].includes(question.compatiblePurchaseFlowType),
      )
      .map((question) => ({
        id: question.fieldId,
        title: question.title,
        subtitle: question.type === 'simple_checkbox' ? null : question.subtitle,
        label: question.label,
        type: question.type,
        properties: {
          ...(question.properties || {}),
          choices: question.choices,
          disabled: orderPlaced,
          children: question.type === 'simple_checkbox' ? question.subtitle : null,
          dataDogMasked: !!question.encrypted,
        },
        validations:
          question.type === 'simple_checkbox'
            ? [...question.validations, 'legal']
            : question.validations,
      })),
  ];
};

const getRetailFormFields = ({
  formikBag,
  values,
  leaseStartDate,
  utilityProvider,
  utilityUser,
  orderPlaced,
  termsAndConditions,
}) => {
  const isAmericanExpress = checkIfIsAmericanExpressCard(values.cardNumber);
  const isValidPhoneNumber = isUserValidPhoneNumber(utilityUser);
  return [
    {
      id: 'utilityProviderPlanId',
      type: 'hidden',
    },
    {
      id: 'utilityProviderPlanVariantId',
      type: 'hidden',
    },
    showFieldIf(!isValidPhoneNumber, {
      id: 'verifiedPhoneNumber',
      title: 'Confirm your phone number',
      type: 'short_text',
      properties: {
        placeholder: 'Type your phone number here...',
      },
      validations: ['required', 'phone'],
    }),
    ...utilityProvider.questions
      .filter((question) =>
        [
          PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_RETAIL,
          PROVIDER_QUESTION_PURCHASE_FLOW_TYPE_ALL,
        ].includes(question.compatiblePurchaseFlowType),
      )
      .map((question) => ({
        id: question.fieldId,
        title: question.title,
        subtitle: question.type === 'simple_checkbox' ? null : question.subtitle,
        label: question.label,
        type: question.type,
        properties: {
          ...(question.properties || {}),
          choices: question.choices,
          disabled: orderPlaced,
          children: question.type === 'simple_checkbox' ? question.subtitle : null,
          dataDogMasked: !!question.encrypted,
          isValidDate: (current) => {
            if (!question.properties?.afterLeaseStart || !leaseStartDate) {
              return true;
            }
            return current
              .startOf('day')
              .isSameOrAfter(dateStringMomentWithCurrentTime(leaseStartDate).startOf('day'));
          },
        },
        validations:
          question.type === 'simple_checkbox'
            ? [...question.validations, 'legal']
            : question.validations,
      })),
    showFieldIf(utilityProvider.ccRequired, {
      title: 'Address Line 2',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 2 here...',
        dataDogMasked: true,
      },
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressLine1',
      title: 'Address Line 1',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 1 here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressLine2',
      title: 'Address Line 2',
      type: 'short_text',
      properties: {
        placeholder: 'Type address line 2 here...',
        dataDogMasked: true,
      },
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressCity',
      title: 'City',
      type: 'short_text',
      properties: {
        placeholder: 'Type city here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardAddressState',
      title: 'State',
      type: 'short_text',
      properties: {
        placeholder: 'Type state here...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardName',
      title: 'Name on Card',
      type: 'short_text',
      properties: {
        placeholder: 'Type the name on your card...',
        dataDogMasked: true,
      },
      validations: ['required'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cardNumber',
      title: 'Card Number',
      type: 'card_number',
      properties: {
        placeholder: 'Type card number here...',
        onlyAcceptNumbers: true,
        length: isAmericanExpress ? 15 : 16,
        lengthErrorMessage: 'Your card number is incomplete.',
        dataDogMasked: true,
        widget:
          formikBag && formikBag.touched?.cardNumber && !isCardNumber(values.cardNumber) ? (
            <SrBox margin="3px 0 12px">
              <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                <SrTextGreycliff color="white" weight="700">
                  {'Check Your Card Number: '}
                </SrTextGreycliff>
                <SrTextGreycliff color="white" weight="600">
                  This card type isn't recognized. Double-check your number or proceed anyway.
                </SrTextGreycliff>
              </SrErrorMessage>
            </SrBox>
          ) : null,
      },
      validations: ['required', 'length'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'expirationDate',
      title: 'Expiration Date',
      type: 'date_expiration_input',
      properties: {
        placeholder: 'Type expiration date here...',
        dateFormat: 'MM/YY',
        dataDogMasked: true,
        widget:
          formikBag &&
          formikBag.touched?.expirationDate &&
          isCardExpirationSameYear(values.expirationDate) ? (
            <SrBox margin="3px 0 12px">
              <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                <SrTextGreycliff color="white" weight="700">
                  {'Check Your Expiration Date: '}
                </SrTextGreycliff>
                <SrTextGreycliff color="white" weight="600">
                  Your card expires soon. Please double-check if the year is correct because some
                  internet companies may reject it.
                </SrTextGreycliff>
              </SrErrorMessage>
            </SrBox>
          ) : null,
      },
      validations: ['required', 'noPastDates'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'cvc',
      title: 'Security Code',
      type: 'card_cvc',
      properties: {
        placeholder: 'Type security code here...',
        onlyAcceptNumbers: true,
        length: isAmericanExpress ? 4 : 3,
        lengthErrorMessage: "Your card's security code is incomplete.",
        dataDogMasked: true,
      },
      validations: ['required', 'length'],
    }),
    showFieldIf(utilityProvider.ccRequired, {
      id: 'zipCode',
      title: 'ZIP Code Associated With Card',
      type: 'zipCode',
      properties: {
        placeholder: 'Type ZIP code here...',
        dataDogMasked: true,
      },
      validations: ['required', 'zipCode'],
    }),
    {
      id: 'agreeToTermsAndConditions',
      title: 'Terms & Conditions',
      type: 'markdown_checkbox',
      properties: {
        children: (
          <SrText fontSize="15px" font="helvetica" lineHeight="20px" weight="500">
            I have read and agree to the Terms & Conditions.
          </SrText>
        ),
        className: 'adobeCheckboxSquare16 helvetica',
        markdown: {
          theme: SR_MARKDOWN_THEME_OUTLINED_HELVETICA,
          source: termsAndConditions,
          styles: {
            height: '168px',
            overflowX: 'scroll',
          },
        },
      },
      validations: ['required', 'legal'],
    },
  ];
};

const getFormFields = ({
  formikBag,
  values,
  leaseStartDate,
  utilityProvider,
  orderPlaced,
  utilityUser,
  termsAndConditions,
  isCircleCI,
  isInternetAmenityPlan,
  isHybridPurchaseFlowType,
  termsAndConditionsHybridPlans,
}) => {
  if (isInternetAmenityPlan || isHybridPurchaseFlowType) {
    return getInternetAmenityFormFields({
      utilityProvider,
      orderPlaced,
      values,
      formikBag,
      termsAndConditionsHybridPlans,
    });
  }

  return getRetailFormFields({
    formikBag,
    values,
    leaseStartDate,
    utilityProvider,
    utilityUser,
    orderPlaced,
    termsAndConditions,
    isCircleCI,
  });
};

const getTitle = ({ orderPlaced, isInternetAmenityPlan, isTransferToPurchase }) => {
  if (orderPlaced) {
    return isInternetAmenityPlan ? 'Internet Plan Details' : 'Confirm order details';
  }
  if (isTransferToPurchase) {
    return 'Confirm your transfer';
  }
  return isInternetAmenityPlan ? 'Confirm order details' : 'Complete your purchase';
};

const getCreditCardLegend = ({ isInternetAmenityPlan, isHybridPurchaseFlowType }) => {
  if (isHybridPurchaseFlowType) {
    return 'Your card will be charged monthly for regular billing services';
  }

  if (isInternetAmenityPlan) {
    return (
      'Your card will only be charged for unreturned equipment ' +
      'or extra services; regular billing is managed by your property manager.'
    );
  }

  return '🔒 Secure encrypted payment';
};

const SrUtilitySetupProviderPlanConfirmation = ({
  utilityActivationItem,
  utilityActivation,
  utilityProviders,
  utilityProviderId,
  utilityProviderPlanId,
  utilityProviderPlanVariantId,
  internetAmenityPlanId,
  isTransferToPurchase,
  leaseStartDate,
  isLoading,
  onSubmit,
}) => {
  const { isCircleCI } = useBranch({
    isCircleCI: ['env', 'CIRCLECI'],
  });

  const utilityUser = utilityActivation.user;
  const utilityProvider =
    utilityProviders.find((provider) => provider.id === utilityProviderId) ||
    utilityActivationItem.provider;

  const utilityProviderPlan =
    (utilityProvider.plansAvailable || utilityProvider.plans).find((plan) => {
      return (
        plan.id === utilityProviderPlanId &&
        (!plan.utilityProviderPlanVariantId ||
          plan.utilityProviderPlanVariantId === utilityProviderPlanVariantId)
      );
    }) || utilityActivationItem.plan;
  const isInternetAmenityPlan =
    utilityActivation.hasInternetAmenityEligibility && !!internetAmenityPlanId;
  const isHybridPurchaseFlowType =
    utilityProvider.purchaseFlowType === UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID &&
    !!internetAmenityPlanId;

  const orderPlaced = !!utilityActivationItem.orderPlacedAt;
  let termsAndConditions = lodashTemplate(
    UTILITY_ACTIVATION_ITEM_PURCHASE_REQUEST_TERMS_AND_CONDITIONS,
  )({
    utilityPartnerName: utilityActivation.partner?.name,
  });

  if (utilityProvider.id === UTILITY_PROVIDER_ID_XFINITY) {
    termsAndConditions = `${XFINITY_ADDITIONAL_TERMS}\n\n${termsAndConditions}`;
  }

  let termsAndConditionsHybridPlans = lodashTemplate(
    UTILITY_ACTIVATION_ITEM_PURCHASE_REQUEST_TERMS_AND_CONDITIONS_HYBRID_PLANS,
  )({
    utilityPartnerName: utilityActivation.partner?.name,
  });

  if (utilityProvider.id === UTILITY_PROVIDER_ID_XFINITY) {
    termsAndConditionsHybridPlans = `${XFINITY_ADDITIONAL_TERMS}\n\n${termsAndConditionsHybridPlans}`;
  }

  const defaultValues = {
    utilityProviderPlanId,
    utilityProviderPlanVariantId,
    ...(utilityActivationItem.utilityProviderAnswers || {}),
    addressMovingTo: utilityActivation.address?.display,
    addressUnitMovingTo: utilityActivation.address?.unitNumber,
    agreeToTermsAndConditions: utilityActivationItem.agreeToTermsAndConditions,
    internetAmenityPlanId: internetAmenityPlanId,
  };

  const providerTheme = utilityProvider.theme || {};

  const getButtonText = () => {
    if (isHybridPurchaseFlowType) {
      return 'Pay & Schedule Delivery';
    }
    if (isInternetAmenityPlan) {
      return 'Finish & Schedule Delivery';
    }
    if (isTransferToPurchase) {
      return 'Complete Transfer';
    }
    return 'Confirm Order';
  };

  const PlanCardComponent =
    isInternetAmenityPlan && !isHybridPurchaseFlowType
      ? SrUtilityProfitAmenityConfirmationPlanCard
      : SrUtilityProfitProviderPlanCard;

  useEffect(() => {
    const experimentVariants = getParsedExperimentVariants();
    const eventPayload = {
      user: utilityActivation.user,
      experimentVariants,
      utilityActivationId: utilityActivation.id,
      utilityProviderId,
      utilityProviderPlanId,
      utilityProviderPlanVariantId,
      internetAmenityPlanId,
      ...(utilityActivation.partner && {
        utilityPartnerId: utilityActivation.partner.id,
        utilityPartnerSlug: utilityActivation.partner.slug,
      }),
    };

    analytics.track('UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION_LANDED', eventPayload);
  }, [
    utilityActivation,
    utilityProviderId,
    utilityProviderPlanId,
    utilityProviderPlanVariantId,
    internetAmenityPlanId,
  ]);

  const handleSubmit = (formikBag) => {
    const experimentVariants = getParsedExperimentVariants();
    const eventPayload = {
      user: utilityActivation.user,
      experimentVariants,
      utilityActivationId: utilityActivation.id,
      utilityProviderId,
      utilityProviderPlanId,
      utilityProviderPlanVariantId,
      internetAmenityPlanId,
      ...(utilityActivation.partner && {
        utilityPartnerId: utilityActivation.partner.id,
        utilityPartnerSlug: utilityActivation.partner.slug,
      }),
    };

    analytics.track('UTILITY_SETUP_PROVIDER_PLAN_CONFIRM_ORDER_CLICKED', eventPayload);

    validateFormFieldsAndSubmit({
      formikBag,
      formFields: getFormFields({
        values: formikBag.values,
        leaseStartDate,
        utilityUser,
        utilityProvider,
        termsAndConditions,
        isCircleCI,
        isInternetAmenityPlan,
        isHybridPurchaseFlowType,
        termsAndConditionsHybridPlans,
      }),
    });
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({
        formFields: getFormFields({
          values: defaultValues,
          leaseStartDate,
          utilityUser,
          utilityProvider,
          termsAndConditions,
          isCircleCI,
          isInternetAmenityPlan,
          isHybridPurchaseFlowType,
          termsAndConditionsHybridPlans,
        }),
        defaultValues: defaultValues,
      })}
      validate={(formValues) => {
        const formFields = getFormFields({
          values: formValues,
          leaseStartDate,
          utilityUser,
          utilityProvider,
          termsAndConditions,
          isCircleCI,
          isInternetAmenityPlan,
          isHybridPurchaseFlowType,
          termsAndConditionsHybridPlans,
        });
        return createFormValidationFn(formFields)(formValues);
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          formikBag,
          values: formikBag.values,
          leaseStartDate,
          utilityProvider,
          orderPlaced,
          termsAndConditions,
          isCircleCI,
          isInternetAmenityPlan,
          isHybridPurchaseFlowType,
          termsAndConditionsHybridPlans,
        });
        const { cardFormFieldIds, securityQuestionFieldIds } = getFormFieldIds({
          utilityProvider,
          utilityUser: utilityActivation.user,
          isInternetAmenityPlan,
          isHybridPurchaseFlowType,
        });
        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        const title = getTitle({
          orderPlaced,
          isInternetAmenityPlan,
          isTransferToPurchase,
        });

        const buttonText = getButtonText();

        return (
          <Form styleName="view">
            <div styleName={classNames('titleBox', { isTransferToPurchase })}>
              <span>{title}</span>
            </div>
            <div styleName="contentBox">
              <div styleName="planTitleBox">
                <span>{isInternetAmenityPlan ? '🔑 Your required plan:' : '🔑 Your plan:'}</span>
              </div>
              <PlanCardComponent
                isActive={!isHybridPurchaseFlowType}
                theme={
                  isHybridPurchaseFlowType
                    ? PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY
                    : PROVIDER_PLAN_CARD_THEME_CARRIER
                }
                utilityProviderPlan={utilityProviderPlan}
                utilityProvider={utilityProvider}
                utilityPartner={utilityActivation.partner}
              />
              {securityQuestionFieldIds.length > 0 && (
                <div styleName="cardBox">
                  {isInternetAmenityPlan || isHybridPurchaseFlowType ? (
                    <div styleName="cardTitleBox">
                      <span>🔐 Shipping & Activation</span>
                    </div>
                  ) : (
                    <div styleName="cardTitleBox">
                      <span>🔐 Account Security</span>
                      <SrText
                        fontSize="15px"
                        font="helvetica"
                        lineHeight="20px"
                        weight="500"
                        display="block"
                        paddingTop="8px"
                      >
                        {utilityProvider.title} requires these for login purposes
                      </SrText>
                    </div>
                  )}
                  <div>
                    {securityQuestionFieldIds.map((formFieldId) => {
                      const formField = getFormFieldsById(formFieldId);
                      if (
                        (isInternetAmenityPlan && formFieldId === 'previousAddress') ||
                        !formField
                      ) {
                        return null;
                      }
                      return (
                        <div key={formFieldId}>
                          <SrFormikField
                            formikBag={formikBag}
                            formField={formField}
                            theme={SR_FIELD_THEME_ADOBE}
                            className="utilityProviderQuestion helvetica"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div styleName="securePaymentBox">
                    <span>🔒 Securely encrypted data</span>
                  </div>
                </div>
              )}
              {!orderPlaced && utilityProvider.ccRequired && cardFormFieldIds.length > 0 && (
                <div styleName="cardBox">
                  <div styleName="cardTitleBox">
                    <span>💳 Credit/Debit Card</span>
                    <SrText
                      fontSize="13px"
                      font="helvetica"
                      lineHeight="20px"
                      weight="500"
                      display="block"
                      paddingTop="8px"
                    >
                      {getCreditCardLegend({ isInternetAmenityPlan, isHybridPurchaseFlowType })}
                    </SrText>
                  </div>
                  <div>
                    {cardFormFieldIds.map((formFieldId) => (
                      <SrFormikField
                        key={formFieldId}
                        formikBag={formikBag}
                        formField={getFormFieldsById(formFieldId)}
                        theme={SR_FIELD_THEME_ADOBE}
                        className="utilityProviderQuestion helvetica"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!orderPlaced && (
              <SrStaticFooter size="130">
                <SrButton
                  type="submit"
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  margin="0 auto"
                  disabled={isLoading}
                  onClick={() => handleSubmit(formikBag)}
                  width="calc(100% - 20px)"
                  themeOverrides={{
                    backgroundColor: providerTheme.buttonPrimaryColor,
                    color: providerTheme.buttonPrimaryTextColor,
                    borderRadius: '2px',
                    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                  }}
                >
                  {buttonText}
                </SrButton>
              </SrStaticFooter>
            )}
          </Form>
        );
      }}
    />
  );
};

SrUtilitySetupProviderPlanConfirmation.displayName = 'SrUtilitySetupProviderPlanConfirmation';

SrUtilitySetupProviderPlanConfirmation.propTypes = {
  utilityActivationItem: PropTypes.object.isRequired,
  utilityActivation: PropTypes.object.isRequired,
  utilityProviderId: PropTypes.number.isRequired,
  utilityProviderPlanId: PropTypes.number.isRequired,
  utilityProviderPlanVariantId: PropTypes.number,
  internetAmenityPlanId: PropTypes.number,
  isTransferToPurchase: PropTypes.bool.isRequired,
  leaseStartDate: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default SrUtilitySetupProviderPlanConfirmation;
